import React, {useCallback, useContext, useEffect, useMemo, useReducer, useState} from "react";
import {
	Alert,
	AlertTitle,
	Button,
	Chip,
	CircularProgress,
	InputAdornment,
	Skeleton,
	Typography
} from "@mui/material";
import AddCircleOutlineTwoToneIcon from "@mui/icons-material/AddCircleOutlineTwoTone";
import LoginTwoToneIcon from "@mui/icons-material/LoginTwoTone";
import MarkEmailReadTwoToneIcon from "@mui/icons-material/MarkEmailReadTwoTone";
import SaveTwoToneIcon from "@mui/icons-material/SaveTwoTone";
import SearchTwoToneIcon from "@mui/icons-material/SearchTwoTone";
import DeleteTwoToneIcon from "@mui/icons-material/DeleteTwoTone";
import moment from "moment";
import {useHistory, useLocation} from "react-router-dom";
import {
	addRolesToUser,
	getAllCompanies,
	loginUser,
	removeRoleFromUser
} from "../../../services/AuthServices";
import {getUsersByCompany, sendResetEmail} from "../../../services/ReferenteServices";
import {AppContext, AuthContext} from "../../../App";
import {getCompanyInfo} from "../../../utils/GenericUtils";
import {roles} from "../../../utils/dictionaries/Roles";
import {setOpenSnackbar} from "../../../services/AppServices";
import {initReferenteState, REFERENTE_INITIAL_STATE, ReferenteReducer} from "../../../reducers/ReferenteReducer";
import AppPage from "../../widgets/layout/AppPage";
import SimpleDialog from "../../widgets/dialogs/SimpleDialog";
import UserDrawer from "./drawer/UserDrawer";
import UtenteCard from "./detail/UtenteCard";
import {UserStyle} from "../../../styles/pages/users/UserStyle";
import AutocompleteSelect from "../../widgets/inputs/AutocompleteSelect";
import SolidTextField from "../../widgets/inputs/SolidTextField";

const ROLE_PREFIX = "ROLE_";

const formatDate = (value) => {
	const date = moment(value);
	return date.isValid() ? date.format("DD/MM/YYYY HH:mm") : "Non disponibile";
};

const getRoleLabel = (role) => roles[role]?.label || role?.replaceAll("ROLE_", "") || role;

const getUserKey = (user) => user?.id || user?.username || user?.alias;

const normalizeRole = (value) => {
	const normalizedValue = String(value || "")
		.trim()
		.toUpperCase()
		.replace(/\s+/g, "_")
		.replace(/[^A-Z0-9_]/g, "_")
		.replace(/_+/g, "_");
	const roleSuffix = normalizedValue.startsWith(ROLE_PREFIX)
		? normalizedValue.slice(ROLE_PREFIX.length)
		: normalizedValue;
	const cleanedSuffix = roleSuffix.replace(/^_+|_+$/g, "");

	return cleanedSuffix ? `${ROLE_PREFIX}${cleanedSuffix}` : "";
};

const normalizeRoles = (selectedRoles = []) => Array.from(new Set(
	selectedRoles.map(normalizeRole).filter(Boolean)
));

const normalizeCustomRoleInput = (value) => {
	const normalizedValue = String(value || "")
		.toUpperCase()
		.replace(/\s+/g, "_")
		.replace(/[^A-Z0-9_]/g, "_")
		.replace(/_+/g, "_");

	if (!normalizedValue.startsWith(ROLE_PREFIX)) {
		return `${ROLE_PREFIX}${normalizedValue.replace(/^ROLE_?/, "").replace(/^_+/, "")}`;
	}

	return `${ROLE_PREFIX}${normalizedValue.slice(ROLE_PREFIX.length).replace(/^_+/, "")}`;
};

const getRoleOptions = (selectedRoles = []) => normalizeRoles([...Object.keys(roles), ...selectedRoles])
	.map((role) => ({
		value: role,
		label: getRoleLabel(role),
		description: roles[role]?.description || role,
		color: roles[role]?.color || "var(--atl-blue)",
	}));

const decodePassword = (value) => {
	try {
		return atob(value || "");
	} catch (error) {
		return "";
	}
};

export const GestioneUtenti = () => {
	const classes = UserStyle();
	const authContext = useContext(AuthContext);
	const appContext = useContext(AppContext);
	const [state, dispatch] = useReducer(ReferenteReducer, REFERENTE_INITIAL_STATE, initReferenteState);
	const history = useHistory();
	const location = useLocation();
	const [value, setValue] = useState("");
	const [stato, setStato] = useState(null);
	const [userSelected, setUserSelected] = useState(null);
	const [submitGet, setSubmitGet] = useState(false);
	const [submitUpdate, setSubmitUpdate] = useState(false);
	const [userRoles, setRoles] = useState([]);
	const [customRole, setCustomRole] = useState(ROLE_PREFIX);
	const [submit, setSubmit] = useState(false);

	const users = useMemo(() => authContext.state.users || [], [authContext.state.users]);
	const roleOptions = useMemo(() => getRoleOptions(userRoles), [userRoles]);
	const customRoleToAdd = useMemo(() => normalizeRole(customRole), [customRole]);
	const filteredUsers = useMemo(() => {
		const search = value.trim().toUpperCase();
		if (!search) return users;

		return users.filter((item) => (
			item.alias?.toUpperCase()?.includes(search)
			|| item.roles?.some((role) => role.toUpperCase().includes(search) || getRoleLabel(role).toUpperCase().includes(search))
			|| item.username?.toUpperCase()?.includes(search)
			|| item.gruppo?.toUpperCase()?.includes(search)
		));
	}, [users, value]);

	const activeUsers = useMemo(() => users.filter((user) => user.trusted).length, [users]);
	const onlineUsers = useMemo(() => users.filter((user) => user.online).length, [users]);
	const deletedUsers = Math.max(users.length - activeUsers, 0);

	const selectedCompanyId = stato?.id || getCompanyInfo()?.id;

	const handleSearch = (event) => {
		setValue(event.target.value);
	};

	const removeRole = (user, role) => {
		const nextRoles = (userRoles || []).filter((item) => item !== role);
		setRoles(nextRoles);
		setUserSelected((prevSelected) => prevSelected ? {...prevSelected, roles: nextRoles} : prevSelected);
		removeRoleFromUser(user, [role], authContext.dispatch, selectedCompanyId);
	};

	const inviaCredenziali = () => {
		if (!userSelected?.username) return;
		sendResetEmail(userSelected.username, appContext.dispatch);
		setSubmit(true);
	};

	useEffect(() => {
		if (submit && !state.loadingSendEmail && state.errorSendEmail === "") {
			setOpenSnackbar("success", "Email inviata con successo", appContext.dispatch);
			setSubmit(false);
		} else if (submit && !state.loadingSendEmail && state.errorSendEmail.length > 1) {
			setOpenSnackbar("error", state.errorSendEmail, appContext.dispatch);
			setSubmit(false);
		}
	}, [submit, appContext.dispatch, state.errorSendEmail, state.loadingSendEmail]);

	const selectUser = useCallback((user) => {
		setUserSelected(user);
		setRoles(normalizeRoles(user.roles || []));
		setCustomRole(ROLE_PREFIX);
	}, []);

	const logWithUserCredential = useCallback((username, password) => {
		const decodedPassword = decodePassword(password);
		if (!username || !decodedPassword) {
			setOpenSnackbar("error", "Password utente non disponibile.", appContext.dispatch);
			return;
		}

		loginUser({codF: username, password: decodedPassword}, authContext.dispatch, location, history);
	}, [authContext.dispatch, appContext.dispatch, history, location]);

	const selectMenuItem = useCallback((item) => {
		setStato(item);
		getUsersByCompany(item.id, authContext.dispatch);
	}, [authContext.dispatch]);

	const handleRolesChange = (newRoles) => {
		setRoles(normalizeRoles(newRoles));
	};

	const addCustomRole = () => {
		if (!customRoleToAdd) return;
		if (userRoles.includes(customRoleToAdd)) {
			setOpenSnackbar("info", "Ruolo gia presente nella selezione", appContext.dispatch);
			return;
		}

		setRoles((prevRoles) => normalizeRoles([...prevRoles, customRoleToAdd]));
		setCustomRole(ROLE_PREFIX);
	};

	const handleCustomRoleKeyDown = (event) => {
		if (event.key === "Enter" || event.key === "," || event.key === "Tab") {
			event.preventDefault();
			addCustomRole();
		}
	};

	const modifyUser = useCallback(() => {
		if (!userSelected || !selectedCompanyId) return;
		setSubmitUpdate(true);
		addRolesToUser(userSelected, normalizeRoles(userRoles), authContext.dispatch, selectedCompanyId);
	}, [userRoles, userSelected, authContext.dispatch, selectedCompanyId]);

	useEffect(() => {
		setSubmitGet(true);
		getAllCompanies(authContext.dispatch);
	}, [authContext.dispatch]);

	useEffect(() => {
		if (submitGet && !authContext.state.loadingGetCompanies && authContext.state.errorGetCompanies === "" && authContext.state.companies.length) {
			setSubmitGet(false);
			selectMenuItem(authContext.state.companies[0]);
		}
	}, [
		submitGet,
		authContext.state.loadingGetCompanies,
		authContext.state.errorGetCompanies,
		authContext.state.companies,
		selectMenuItem,
	]);

	useEffect(() => {
		if (submitUpdate && !authContext.state.loadingAddRole && authContext.state.errorAddRole === "") {
			const nextRoles = normalizeRoles(userRoles);
			setSubmitUpdate(false);
			setUserSelected((prevSelected) => prevSelected ? {...prevSelected, roles: nextRoles} : prevSelected);
			getUsersByCompany(selectedCompanyId, authContext.dispatch);
			setOpenSnackbar("success", "Utente modificato con successo", appContext.dispatch);
		} else if (submitUpdate && !authContext.state.loadingAddRole && authContext.state.errorAddRole !== "") {
			setSubmitUpdate(false);
			setOpenSnackbar("error", authContext.state.errorAddRole, appContext.dispatch);
		}
	}, [
		submitUpdate,
		appContext.dispatch,
		authContext.dispatch,
		authContext.state.errorAddRole,
		authContext.state.loadingAddRole,
		selectedCompanyId,
		userRoles,
	]);

	useEffect(() => {
		if (!userSelected || authContext.state.loadingGetAllUsers) return;
		const updatedUser = users.find((user) => getUserKey(user) === getUserKey(userSelected));
		if (!updatedUser || updatedUser === userSelected) return;

		setUserSelected(updatedUser);
		setRoles(normalizeRoles(updatedUser.roles || []));
	}, [authContext.state.loadingGetAllUsers, userSelected, users]);

	return (
		<React.Fragment>
			<AppPage
				title="Gestione utenti"
				mainClassName={classes.page}
				containerClassName={classes.container}
			>
				<section className={classes.hero}>
					<div>
						<Typography className={classes.eyebrow}>Atlantis workspace</Typography>
						<Typography component="h1" className={classes.heroTitle}>
							Gestione utenti
						</Typography>
						<Typography className={classes.heroSubtitle}>
							Amministra utenti, aziende, accessi e ruoli con una vista piu ordinata e rapida da scansionare.
						</Typography>
					</div>
					<div className={classes.heroStats}>
						<div className={classes.heroStat}>
							<Typography className={classes.statLabel}>Totali</Typography>
							<Typography className={classes.statValue}>{users.length}</Typography>
						</div>
						<div className={classes.heroStat}>
							<Typography className={classes.statLabel}>Online</Typography>
							<Typography className={classes.statValue}>{onlineUsers}</Typography>
						</div>
						<div className={classes.heroStat}>
							<Typography className={classes.statLabel}>Cancellati</Typography>
							<Typography className={classes.statValue}>{deletedUsers}</Typography>
						</div>
					</div>
				</section>

				<section className={classes.searchPanel}>
					<SolidTextField
						className={classes.searchField}
						placeholder="Cerca per alias, username, ruolo o gruppo"
						value={value}
						onChange={handleSearch}
						autoFocus
						fullWidth
						size="small"
						variant="outlined"
						slotProps={{
							input: {
								startAdornment: (
									<InputAdornment position="start">
										<SearchTwoToneIcon fontSize="small"/>
									</InputAdornment>
								),
							},
						}}
					/>
				</section>

				<div className={classes.contentShell}>
					<UserDrawer classes={classes} selectMenuItem={selectMenuItem} stato={stato}/>

					<section className={classes.usersPanel}>
						<header className={classes.usersPanelHeader}>
							<div>
								<Typography className={classes.panelTitle}>
									{stato?.nome || "Utenti"}
								</Typography>
								<Typography className={classes.panelSubtitle}>
									Risultati filtrati per azienda selezionata e ricerca corrente.
								</Typography>
							</div>
							<span className={classes.resultPill}>
								{filteredUsers.length} risultati
							</span>
						</header>

						{authContext.state.loadingGetAllUsers && (
							<div className={classes.usersGrid}>
								{[0, 1, 2, 3, 4, 5].map((item) => (
									<Skeleton key={item} className={classes.skeletonCard} variant="rounded"/>
								))}
							</div>
						)}

						{!authContext.state.loadingGetAllUsers && authContext.state.errorGetAllUsers !== "" && (
							<Alert severity="error" sx={{borderRadius: 4}}>
								<AlertTitle>Errore</AlertTitle>
								Non e stato possibile recuperare gli utenti. Riprova tra qualche minuto.
							</Alert>
						)}

						{!authContext.state.loadingGetAllUsers && authContext.state.errorGetAllUsers === "" && filteredUsers.length === 0 && (
							<Typography className={classes.emptyState}>
								Nessun utente trovato con i filtri correnti.
							</Typography>
						)}

						{!authContext.state.loadingGetAllUsers && authContext.state.errorGetAllUsers === "" && filteredUsers.length > 0 && (
							<div className={classes.usersGrid}>
								{filteredUsers.map((user) => (
									<UtenteCard
										key={user.id || user.username || user.alias}
										classes={classes}
										user={user}
										setUserSelected={selectUser}
									/>
								))}
							</div>
						)}
					</section>
				</div>
			</AppPage>

			{userSelected && (
				<SimpleDialog
					title="Modifica utente"
					maxWidth="lg"
					open={Boolean(userSelected)}
					handleClose={() => {
						setUserSelected(null);
						setCustomRole(ROLE_PREFIX);
					}}
					showCloseButton
					content={(
						<div className={classes.dialogContent}>
							<div className={classes.dialogSummaryGrid}>
								<div className={classes.dialogFact}>
									<Typography className={classes.dialogFactLabel}>Alias</Typography>
									<strong className={classes.dialogFactValue}>{userSelected.alias || "Non disponibile"}</strong>
								</div>
								<div className={classes.dialogFact}>
									<Typography className={classes.dialogFactLabel}>Username</Typography>
									<strong className={classes.dialogFactValue}>{userSelected.username || "Non disponibile"}</strong>
								</div>
								<div className={classes.dialogFact}>
									<Typography className={classes.dialogFactLabel}>Gruppo</Typography>
									<strong className={classes.dialogFactValue}>{userSelected.gruppo || "Non indicato"}</strong>
								</div>
								<div className={classes.dialogFact}>
									<Typography className={classes.dialogFactLabel}>Registrazione</Typography>
									<strong className={classes.dialogFactValue}>{formatDate(userSelected.dataRegistrazione)}</strong>
								</div>
								<div className={classes.dialogFact}>
									<Typography className={classes.dialogFactLabel}>Ultimo accesso</Typography>
									<strong className={classes.dialogFactValue}>{formatDate(userSelected.ultimoAccesso)}</strong>
								</div>
								<div className={classes.dialogFact}>
									<Typography className={classes.dialogFactLabel}>Scadenza sessione</Typography>
									<strong className={classes.dialogFactValue}>{formatDate(userSelected.scadenzaSessione)}</strong>
								</div>
							</div>

							<section className={classes.dialogSection}>
								<Typography className={classes.dialogSectionTitle}>Ruoli utente</Typography>
								<div className={classes.dialogRoles}>
									{userRoles.map((role) => (
										<Chip
											key={role}
											className={classes.roleChip}
											label={getRoleLabel(role)}
											onDelete={() => removeRole(userSelected, role)}
											size="small"
											style={{"--role-color": roles[role]?.color || "var(--atl-blue)"}}
										/>
									))}
								</div>
								<AutocompleteSelect
									className={classes.dialogRoleField}
									label="Ruoli da assegnare"
									multiple
									name="roles"
									options={roleOptions}
									value={userRoles}
									onChange={handleRolesChange}
									renderOptionContent={(option) => (
										<div>
											<Typography variant="body2" style={{fontWeight: 850}}>
												{option.label}
											</Typography>
											<Typography variant="caption" color="textSecondary">
												{option.description}
											</Typography>
										</div>
									)}
									renderTags={(value, getTagProps) => value.map((option, index) => (
										<Chip
											{...getTagProps({index})}
											className={classes.roleChip}
											key={option.value}
											label={option.label}
											size="small"
											style={{"--role-color": option.color}}
										/>
									))}
									size="small"
								/>
								<div className={classes.dialogRoleTools}>
									<SolidTextField
										className={classes.dialogRoleField}
										helperText="Aggiungi un ruolo non presente in elenco"
										label="Ruolo custom"
										onChange={(event) => setCustomRole(normalizeCustomRoleInput(event.target.value))}
										handleKeyDown={handleCustomRoleKeyDown}
										size="small"
										value={customRole}
										variant="outlined"
									/>
									<Button
										className={classes.dialogButton}
										disabled={!customRoleToAdd || userRoles.includes(customRoleToAdd)}
										disableElevation
										onClick={addCustomRole}
										size="small"
										startIcon={<AddCircleOutlineTwoToneIcon/>}
										variant="outlined"
									>
										Aggiungi
									</Button>
								</div>
							</section>

							<div className={classes.dialogActions}>
								<Button
									className={classes.dialogButton}
									disableElevation
									onClick={inviaCredenziali}
									size="small"
									startIcon={<MarkEmailReadTwoToneIcon/>}
									variant="outlined"
								>
									Invia credenziali
								</Button>
								<Button
									className={classes.dialogButton}
									disableElevation
									disabled={submitUpdate}
									onClick={modifyUser}
									size="small"
									startIcon={submitUpdate ? <CircularProgress size={16}/> : <SaveTwoToneIcon/>}
									variant="contained"
								>
									Salva ruoli
								</Button>
								<Button
									className={classes.dialogButton}
									disableElevation
									onClick={() => logWithUserCredential(userSelected.username, userSelected.password)}
									size="small"
									startIcon={<LoginTwoToneIcon/>}
									variant="outlined"
								>
									Login utente
								</Button>
								<Button
									className={classes.dialogButton}
									color="error"
									disableElevation
									onClick={() => alert("Lavori in corso: meglio disabilitare l'utente invece di eliminarlo definitivamente.")}
									size="small"
									startIcon={<DeleteTwoToneIcon/>}
									variant="outlined"
								>
									Elimina
								</Button>
							</div>
						</div>
					)}
				/>
			)}
		</React.Fragment>
	);
};
